import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { differenceInDays, format } from 'date-fns';
import {
  CheckCircle,
  Loader2,
  PlusCircle,
  Target
} from 'lucide-react';
import React, { useState } from 'react';
import { WritingGoal } from '../types';
import GoalActionMenu from './goal-action-menu';

interface GoalsTabProps {
  goals: WritingGoal[];
  creatingGoal: boolean;
  setGoalDialogOpen: (open: boolean) => void;
  handleUpdateProgress: (goal: WritingGoal) => void;
  handleEditGoal: (goal: WritingGoal) => void;
  handleDeleteGoal: (goal: WritingGoal) => void;
}

/**
 * Goals Tab Component
 *
 * Displays user's writing goals with filtering by status (active, overdue, completed)
 * and provides actions to create, edit, complete, and delete goals
 */
const GoalsTab: React.FC<GoalsTabProps> = ({
  goals,
  creatingGoal,
  setGoalDialogOpen,
  handleUpdateProgress,
  handleEditGoal,
  handleDeleteGoal
}) => {
  const [activeTab, setActiveTab] = useState<string>('all');

  // Count goals by status
  const activeGoals = goals.filter(g => !g.completed && new Date(g.deadline) >= new Date());
  const overdueGoals = goals.filter(g => !g.completed && new Date(g.deadline) < new Date());
  const completedGoals = goals.filter(g => g.completed);

  // We'll use activeGoals directly in the component

  // We'll use these counts directly in the UI

  // Get goals based on active tab
  const getFilteredGoals = () => {
    switch (activeTab) {
      case 'active':
        return activeGoals;
      case 'overdue':
        return overdueGoals;
      case 'completed':
        return completedGoals;
      default:
        return goals;
    }
  };

  // We're using a simplified approach without detailed deadline status

  return (
    <div className="w-full">
      {/* Simple header with filter tabs */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-1">
          <button
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${activeTab === 'all' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('all')}
          >
            All
          </button>
          <button
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${activeTab === 'active' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('active')}
          >
            Active
          </button>
          <button
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${activeTab === 'overdue' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('overdue')}
          >
            Overdue
          </button>
          <button
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${activeTab === 'completed' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => setActiveTab('completed')}
          >
            Completed
          </button>
        </div>

        <Button
          onClick={() => setGoalDialogOpen(true)}
          disabled={creatingGoal}
          size="sm"
          className="h-8 px-3"
        >
          {creatingGoal ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PlusCircle className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Goals list */}
      {goals.length > 0 ? (
        <div className="border rounded-md overflow-hidden">
          {getFilteredGoals()
            .sort((a, b) => {
              // Sort by completion status first, then by deadline
              if (a.completed && !b.completed) return 1;
              if (!a.completed && b.completed) return -1;

              // For active/overdue goals, sort by deadline (closest first)
              return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
            })
            .map((goal, index) => {
              // Calculate days remaining or overdue
              const today = new Date();
              const deadline = new Date(goal.deadline);
              const daysDiff = differenceInDays(deadline, today);
              const isOverdue = daysDiff < 0 && !goal.completed;

              // Format deadline text
              let deadlineText = format(deadline, "MMM d");
              if (daysDiff === 0) deadlineText = "Today";
              else if (daysDiff === 1) deadlineText = "Tomorrow";
              else if (daysDiff === -1) deadlineText = "Yesterday";

              return (
                <div
                  key={goal.id}
                  className={`flex items-center px-4 py-3 hover:bg-muted/30 transition-colors ${index !== getFilteredGoals().length - 1 ? 'border-b' : ''}`}
                >
                  {/* Checkbox */}
                  <div
                    className="mr-3 cursor-pointer"
                    onClick={() => handleUpdateProgress(goal)}
                  >
                    {goal.completed ? (
                      <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                        <CheckCircle className="h-3.5 w-3.5" />
                      </div>
                    ) : (
                      <div className={`h-5 w-5 rounded-full border ${isOverdue ? 'border-destructive' : 'border-muted-foreground/50'} hover:border-primary transition-colors`}></div>
                    )}
                  </div>

                  {/* Goal content */}
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-baseline justify-between">
                      <h3 className={`font-medium truncate ${goal.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {goal.name}
                      </h3>
                      <div className="flex items-center">
                        {goal.story_name && (
                          <Badge variant="outline" className="mr-2 px-1.5 py-0 h-5 text-xs font-normal">
                            {goal.story_name}
                          </Badge>
                        )}
                        <span className={`text-xs whitespace-nowrap ${isOverdue ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                          {deadlineText}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action menu */}
                  <GoalActionMenu
                    isCompleted={goal.completed}
                    isOverdue={isOverdue}
                    onComplete={() => handleUpdateProgress(goal)}
                    onEdit={() => handleEditGoal(goal)}
                    onDelete={() => handleDeleteGoal(goal)}
                  />
                </div>
              );
            })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center border rounded-md bg-muted/5">
          <Target className="h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No goals found</h3>
          <p className="text-muted-foreground mb-4">
            {activeTab !== 'all' ? 'Try selecting a different filter' : 'Create your first writing goal'}
          </p>
          {activeTab === 'all' && (
            <Button onClick={() => setGoalDialogOpen(true)} size="sm">
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Goal
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default GoalsTab;
